import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import TableRatesRow from './TableRatesRow';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';

const FirstChoice_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  firstChoiceSpecific, setFirstChoiceSpecific, loadings, setLoadings , insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'first_choice';

  const regionDiscounts = getSafe(() => appConfig.content.specifics.first_choice.region_discounts);
  const groupSizes = getSafe(() => appConfig.content.specifics.first_choice.group_sizes);
  const MHD_Loadings = getSafe(() => appConfig.content.specifics.first_choice.mhd_loadings);

  const [regionDiscount, setRegionDiscount] = useState(getRegionDiscountsLabel()[0]);
  const [groupSize, setGroupSize] = useState(getGroupSizesLabel()[0]);
  const [MHD_Loading, set_MHD_Loading] = useState(get_MHD_LoadingsLabel()[0]);

  const plans = ['first_choice_module_1', 'first_choice_module_2', 'first_choice_module_3', 'first_choice_module_4', 'first_choice_module_5', 'first_choice_module_6'];
  const specific_area_of_coverages = getSafe(() => appConfig.content.set3_area_of_coverage);
  const currencies = getSafe(() => appConfig.content.currencies, []);

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts, loadings, regionDiscount, groupSize, MHD_Loading]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'region_discount', regionDiscount);
      addSelectedOption(id, 'group_size', groupSize);
      addSelectedOption(id, 'mhd_loading', MHD_Loading);

      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
    }, 1000);
  }, [regionDiscount, groupSize, MHD_Loading, specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id], discounts[id], loadings[id]]);

  useEffect(() => {
    setViewSelectedOption(id, 'region_discount', setRegionDiscount);
    setViewSelectedOption(id, 'group_size', setGroupSize);
    setViewSelectedOption(id, 'mhd_loading', set_MHD_Loading);



    setViewSelectedOption(id, 'region_discount', (option_value) => {
      firstChoiceSpecific['region_discount'] = option_value;
      setFirstChoiceSpecific({ ...firstChoiceSpecific });
    });

    setViewSelectedOption(id, 'group_size', (option_value) => {
      firstChoiceSpecific['group_size'] = option_value;
      setFirstChoiceSpecific({ ...firstChoiceSpecific });
    });

    setViewSelectedOption(id, 'mhd_loading', (option_value) => {
      firstChoiceSpecific['mhd_loading'] = option_value;
      setFirstChoiceSpecific({ ...firstChoiceSpecific });
    });


    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id] = option_value;
      setDeductibles(deductibles);
    });
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const module_1 = computedRate(rates[i].module_1);
      const module_1_2 = computedRate(rates[i].module_1 + rates[i].module_2);
      const module_1_2_3 = computedRate(rates[i].module_1 + rates[i].module_2 + rates[i].module_3);
      const module_1_2_3_5 = computedRate(rates[i].module_1 + rates[i].module_2 + rates[i].module_3 + rates[i].module_5);
      const module_1_2_3_5_6 = computedRate(rates[i].module_1 + rates[i].module_2 + rates[i].module_3 + rates[i].module_5 + rates[i].module_6);

      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'module_1': module_1 * totalPercent,
        'module_1_2': module_1_2 * totalPercent,
        'module_1_2_3': module_1_2_3 * totalPercent,
        'module_1_2_3_5': module_1_2_3_5 * totalPercent,
        'module_1_2_3_5_6': module_1_2_3_5_6 * totalPercent
      })
    }

    return computedRates;
  }


  function checkIfThereIsAnySelectedPlan() {
    for (let i = 0; i < selectedPlans.length; i++) {
      if (plans.includes(selectedPlans[i])) {
        return true;
      }
    }

    return false;
  }

  function getRegionDiscountsLabel() {
    const labels = [];

    for (let regionDiscount in regionDiscounts) {
      labels.push(regionDiscount);
    }

    return labels;
  }

  function getGroupSizesLabel() {
    const labels = [];

    for (let groupSize in groupSizes) {
      labels.push(groupSize);
    }

    return labels;
  }

  function get_MHD_LoadingsLabel() {
    const labels = [];

    for (let MHD_Loading in MHD_Loadings) {
      labels.push(MHD_Loading);
    }

    return labels;
  }

  function computeFirstChoiceRates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'module_1': computedRate(rates[i].module_1),
        'module_2': computedRate(rates[i].module_2),
        'module_3': computedRate(rates[i].module_3),
        'module_4': computedRate(rates[i].module_4),
        'module_5': computedRate(rates[i].module_5),
        'module_6': computedRate(rates[i].module_6)
      })
    }

    return computedRates;
  }

  function computedRate(rate) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }

    const MHD_LoadingValue = MHD_Loadings[MHD_Loading];
    const regionDiscountValue = regionDiscounts[regionDiscount];
    const groupSizeValue = groupSizes[groupSize];

    let total = rate;

    // MHD loadings
    // total = total * (1 + MHD_LoadingValue);

    // region discount
    // total = total * (1 - regionDiscountValue);

    // group size discount
    // total = total * (1 - groupSizeValue);

    return total;
  }

  function onInputDiscountChange(e) {
    let value = e.target.value;

    // Allow only numbers and at most one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimals
    if ((value.match(/\./g) || []).length > 1) {
      value = value.substring(0, value.lastIndexOf('.'));
    }

    // Ensure only two decimal places
    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      value = `${integerPart}.${decimalPart.substring(0, 2)}`;
    }

    // Remove leading zeros (but allow "0" or "0.x" scenarios)
    if (value !== '' && value !== '0' && value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }

    // Limit value to a maximum of 100
    if (parseFloat(value) > 100) {
      value = '100';
    }

    discounts[id] = value;

    setDiscounts({ ...discounts });
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Typography variant='h5' sx={{ color: '#fff', background: '#01579b', px: 2, py: 1 }}>BUPA First Choice</Typography>

        <Box sx={{ p: 2 }}>
          {/* <Grid container alignItems='center'>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Select Plans</Typography>
            </Grid>
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1' />} label="Module 1" />
            </Grid>
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2' />} label="Module 2" />
            </Grid>
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3' />} label="Module 3" />

            </Grid>
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_4' />} label="Module 4" />
            </Grid>``
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3_5' />} label="Module 5" />
            </Grid>
            <Grid item xs={1.5}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3_5_6' />} label="Module 6" />
            </Grid>

            <Grid item xs={12}>
            </Grid>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Deductible</Typography>
            </Grid>
            <Grid item xs={1.8}>
              <CustomSelect2
                value={deductibles[id]}
                onChange={(e) => {
                  // setDeductible(e.target.value);

                  // setDeductibleIndex(getDeductibleIndex(e.target.value));

                  const val = e.target.value;

                  const _deductibles = JSON.parse(JSON.stringify(deductibles));

                  _deductibles[id] = val;

                  setDeductibles(_deductibles);

                  addToPendingTasks([id]);

                }}
                items={getSafe(() => appConfig.content.deductibles.first_choice[currency], [])}
                disabled={loadingStatus[id]}
              />
            </Grid>
            <Grid item xs={1}>
              <Typography fontSize={18} fontWeight={600}>Group Size</Typography>
            </Grid>
            <Grid item xs={1}>
              <CustomSelect2
                value={groupSize}
                onChange={(e) => {
                  setGroupSize(e.target.value);
                }}
                items={getGroupSizesLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>

            <Grid item xs={1.4}>
              <Typography fontSize={18} fontWeight={600}>Underwriting Terms</Typography>
            </Grid>
            <Grid item xs={1.2}>
              <CustomSelect2
                value={MHD_Loading}
                onChange={(e) => {
                  set_MHD_Loading(e.target.value);
                }}
                items={get_MHD_LoadingsLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>

            <Grid item xs={1.2}>
              <Typography fontSize={18} fontWeight={600}>Region Discount</Typography>
            </Grid>
            <Grid item xs={1.8}>
              <CustomSelect2
                value={regionDiscount}
                onChange={(e) => {
                  setRegionDiscount(e.target.value);
                }}
                items={getRegionDiscountsLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>

            <Grid item xs={12}></Grid>
            <Grid item xs={1.5}><Typography fontSize={18} fontWeight={600}>Area Of Coverage</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificAreaOfCoverage[id]}
                onChange={(e) => {
                  specificAreaOfCoverage[id] = e.target.value;
                  setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 40, width: '190px' }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem>
                {
                  specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                    <MenuItem sx={{ display: areaOfCoverage === area_of_coverage ? 'none' : 'block' }} key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
            <Grid item xs={1.5} mt={1}><Typography fontSize={18} fontWeight={600}>Renewal/Further Discount</Typography></Grid>
            <Grid item xs={1} mt={1}>
              <TextField
                size='small'
                sx={{ width: '80px', textAlign: 'end' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={discounts[id]}
                onChange={onInputDiscountChange}
              />
            </Grid>

            <Grid item xs={2} mt={1}>
              <Typography fontSize={18}>
                Asia Pacific Discount = <span display='inline-block' style={{ fontSize: 18, color: '#002047', fontWeight: 600 }}>{regionDiscounts[regionDiscount] * 100}%</span>
              </Typography>
            </Grid>
          </Grid> */}

          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={18} fontWeight={600}>Plan Type</Typography>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1' />} label="Hospitalisation & Inpatient Treatment" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2' />} label="Outpatient Treatment" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3' />} label="Medicine & Appliances" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_4' />} label="Medical Evacuation" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3_5' />} label="Rehabilitation & Nursing" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='first_choice_module_1_2_3_5_6' />} label="Dental & Optical" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Deductible Option</Typography>
                <Grid item xs={1.8}>
                  <CustomSelect2
                    sx={{ minWidth: 190 }}
                    value={deductibles[id]}
                    onChange={(e) => {
                      // setDeductible(e.target.value);

                      // setDeductibleIndex(getDeductibleIndex(e.target.value));

                      const val = e.target.value;

                      const _deductibles = JSON.parse(JSON.stringify(deductibles));

                      _deductibles[id] = val;

                      setDeductibles(_deductibles);

                      addToPendingTasks([id]);

                    }}
                    items={getSafe(() => appConfig.content.deductibles.first_choice[specificCurrency[id]], [])}
                    disabled={loadingStatus[id]}
                  />
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Area Of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {/* <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem> */}
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    setDeductibles(prev => {
                      return {
                        ...prev,
                        [id]: getSafe(() => appConfig.content.deductibles.first_choice[specificCurrency[id]][0]),
                      };
                    });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["USD"].map(currency => (
                      <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Group Size</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={groupSize}
                  onChange={(e) => {
                    setGroupSize(e.target.value);

                    firstChoiceSpecific['group_size'] = e.target.value;
                    setFirstChoiceSpecific(firstChoiceSpecific);

                    addToPendingTasks([id]);
                  }}
                  items={getGroupSizesLabel()}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Underwriting Terms</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={MHD_Loading}
                  onChange={(e) => {
                    set_MHD_Loading(e.target.value);

                    firstChoiceSpecific['mhd_loading'] = e.target.value;
                    setFirstChoiceSpecific(firstChoiceSpecific);

                    addToPendingTasks([id]);
                  }}
                  items={get_MHD_LoadingsLabel()}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Region Discount</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CustomSelect2
                    sx={{ minWidth: 190 }}
                    value={regionDiscount}
                    onChange={(e) => {
                      setRegionDiscount(e.target.value);

                      firstChoiceSpecific['region_discount'] = e.target.value;
                      setFirstChoiceSpecific(firstChoiceSpecific);

                      addToPendingTasks([id]);
                    }}
                    items={getRegionDiscountsLabel()}
                    disabled={loadingStatus[id]}
                  />
                  <Typography fontSize={18}>
                    <span display='inline-block' style={{ fontSize: 18, color: '#002047', fontWeight: 600 }}>{regionDiscounts[regionDiscount] * 100}%</span>
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  sx={{ width: '80px', textAlign: 'end' }}
                  inputProps={{}}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  sx={{ width: '80px', textAlign: 'end' }}
                  inputProps={{}}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={loadings[id]}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
            <CopyExcelDataButton
              elementId='table-first-choice-rates'
              style={{ marginRight: 7 }}
            />
            <CopyImageButton
              elementId='table-first-choice-rates'
              setShowImageCopy={setShowImageCopy}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-first-choice-rates' size='small' sx={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#01579b', color: '#fff' }}>
                  <TableCell style={{ width: 345, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div></TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>

              </TableHead>
              <TableBody>
                <TableRatesRow
                  planId='first_choice_module_1'
                  planName='module_1'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 1</span> <span style={{ fontSize: 14 }}>(Hospitalisation & Inpatient Treatment)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_1') || selectedPlans.includes('first_choice_module_1_2')
                    || selectedPlans.includes('first_choice_module_1_2_3') || selectedPlans.includes('first_choice_module_1_2_3_5')
                    || selectedPlans.includes('first_choice_module_1_2_3_5_6')}
                  labelWidth={345}
                />
                <TableRatesRow
                  planId='first_choice_module_2'
                  planName='module_2'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 2</span> <span style={{ fontSize: 14 }}>(Outpatient Treatment)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_1_2')
                    || selectedPlans.includes('first_choice_module_1_2_3') || selectedPlans.includes('first_choice_module_1_2_3_5')
                    || selectedPlans.includes('first_choice_module_1_2_3_5_6')}
                  labelWidth={345}
                />
                <TableRatesRow
                  planId='first_choice_module_3'
                  planName='module_3'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 3</span> <span style={{ fontSize: 14 }}>(Medicine & Appliances)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_1_2_3') || selectedPlans.includes('first_choice_module_1_2_3_5')
                    || selectedPlans.includes('first_choice_module_1_2_3_5_6')}
                  labelWidth={345}
                />
                <TableRatesRow
                  planId='first_choice_module_4'
                  planName='module_4'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 4</span> <span style={{ fontSize: 14 }}>(Medical Evacuation)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_4')}
                  labelWidth={345}
                />
                <TableRatesRow
                  planId='first_choice_module_5'
                  planName='module_5'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 5</span> <span style={{ fontSize: 14 }}>(Rehabilitation & Nursing)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_1_2_3_5')
                    || selectedPlans.includes('first_choice_module_1_2_3_5_6')}
                  labelWidth={345}
                />
                <TableRatesRow
                  planId='first_choice_module_6'
                  planName='module_6'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Module 6</span> <span style={{ fontSize: 14 }}>(Dental & Optical)</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeFirstChoiceRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('first_choice_module_1_2_3_5_6')}
                  labelWidth={345}
                />

                {/* {
                  checkIfThereIsAnySelectedPlan() == false &&
                  <TableRow>
                    <TableCell>No Plan Selected</TableCell>
                  </TableRow>
                } */}
              </TableBody>
            </Table>

          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>


      </Paper>

    </Box>
  );
});

export default FirstChoice_Rates;